import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, take, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IdentifierType } from '@nimbus-assistant/enums/identifier-type';
import { TeamContactInfo } from '@nimbus-assistant/models/team-info';
import { AppRoutes } from '@nimbus-assistant/enums/app-routes';
import { UrlProtocolMessage } from '@nimbus-assistant/models/web-view-messages/url-protocol-message';
import { CallerIdentifier } from '@nimbus-assistant/models/task-attributes.model';
import { TaskConversationState } from '@nimbus-assistant/models/task.model';
import { IAssistantUserAutoCompleteValueModel } from '@nimbus-assistant/interfaces/assistant-user-autocomplete-value-model';
import { Utilities } from '@nimbus-assistant/utils/utilities';
import { UserAutoCompleteValueType } from '@nimbus-library/features/shared/services/user-search/models/user-auto-complete-value-type';
import { OutboundCallState } from '@nimbus-global/features/shared/models/api/outbound-call-state.model';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { isConsultationSessionInProgress } from '@nimbus-library/features/shared/models/participant-info/consultation-session.utils';
import { compareLocale } from '@nimbus-library/features/localization/utils/active-locale';
import { ApiService } from './api/api.service';
import { SettingsService } from './settings.service';
import { LoggingService } from './logging.service';
import { ErrorService } from './error.service';
import { UserSelectableState, UserStateService } from './user-state.service';
import { AssistantTasksService } from './assistant-tasks.service';
import { localStorageKeys, LocalStorageService } from './local-storage.service';
import { AssistantHub } from './signalr/assistant.hub';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class TaskOutboundService {
  private static readonly outboundBlockingStates: ReadonlyArray<TaskConversationState> = [
    TaskConversationState.PutOnHold,
    TaskConversationState.Transferring,
  ];

  private clickToCallPrefixes: string[] = ['nimbus-call', 'tel', 'callto'];
  private isInRona = false;
  private isOutboundDisabled = true;

  public services$ = new BehaviorSubject<TeamContactInfo[]>([]);
  public clickToCallTarget$ = new BehaviorSubject<string>(null);
  public isAvailableForOutboundCall$ = new BehaviorSubject<boolean>(false);

  /** Single source of truth for whether placing an outbound / call-on-behalf is disabled. */
  public readonly isOutboundDisabled$: Observable<boolean>;

  public constructor(
    private readonly apiService: ApiService,
    private readonly settingsService: SettingsService,
    private readonly router: Router,
    private readonly errorService: ErrorService,
    private readonly userStateService: UserStateService,
    private readonly tasksService: AssistantTasksService,
    private readonly localStorageService: LocalStorageService,
    private readonly loggingService: LoggingService,
    private readonly hub: AssistantHub,
  ) {
    this.settingsService.settings$.pipe(untilDestroyed(this)).subscribe((settings) => {
      if (settings.outboundServices && settings.outboundServices.length > 0) {
        this.services$.next(settings.outboundServices?.sort((a, b) => compareLocale(a.name, b.name)));
      }
    });

    this.hub.availabilityState$
      .pipe(
        filter((a) => !!a),
        untilDestroyed(this),
      )
      .subscribe((availability) => {
        this.isAvailableForOutboundCall$.next(availability.isAvailableForOutboundCall ?? false);
      });

    this.userStateService.isInRona$.pipe(untilDestroyed(this)).subscribe((v) => (this.isInRona = v));

    const noConnection$ = this.userStateService.selectableState$.pipe(
      map((state) => state === UserSelectableState.noConnectionError),
      distinctUntilChanged(),
    );

    this.isOutboundDisabled$ = combineLatest({
      isAvailableForOutboundCall: this.isAvailableForOutboundCall$,
      consultationInProgress: this.tasksService.connectedTask$.pipe(
        map((task) => isConsultationSessionInProgress(task?.consultationSession?.currentState)),
      ),
      inOutboundBlockingState: this.tasksService.connectedTask$.pipe(
        map((task) => TaskOutboundService.outboundBlockingStates.includes(task?.conversationState)),
      ),
      noConnection: noConnection$,
    }).pipe(
      map(
        ({ isAvailableForOutboundCall, consultationInProgress, inOutboundBlockingState, noConnection }) =>
          !isAvailableForOutboundCall || consultationInProgress || inOutboundBlockingState || noConnection,
      ),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.isOutboundDisabled$.pipe(untilDestroyed(this)).subscribe((disabled) => (this.isOutboundDisabled = disabled));
  }

  public processCall(targetOption: IAssistantUserAutoCompleteValueModel, serviceId: string): Observable<void> {
    return this.startOutboundCall(targetOption, serviceId);
  }

  public startOutboundCall(targetOption: IAssistantUserAutoCompleteValueModel, serviceId: string): Observable<void> {
    this.localStorageService.saveData(localStorageKeys.lastUsedOutboundService, serviceId);

    const target: CallerIdentifier = {
      id: targetOption.value,
      type: targetOption.type === UserAutoCompleteValueType.GUID ? IdentifierType.Guid : IdentifierType.Pstn,
    };

    return this.apiService
      .startOutboundCall({
        teamId: serviceId,
        callerIdentifier: target,
      })
      .pipe(
        catchError((err) => {
          this.loggingService.Info(`CallAs failed: ${JSON.stringify(err)}`);
          return of(null);
        }),
      );
  }

  public processClickToCall(msg: UrlProtocolMessage): void {
    // Initial server availability can briefly be available when reconnecting in persistent RONA:
    // the first fetch does not gate on RONA, only the streamed hot path does.
    if (this.isInRona) {
      return;
    }

    if (this.isOutboundDisabled) {
      return;
    }

    const await_auth_timeout = 3000;
    const settingsReceived$ = this.settingsService.settings$.pipe(
      filter((x) => !!x && !x.isDefault),
      timeout(await_auth_timeout),
      catchError(() => {
        this.errorService.showError(TranslationKeys.WarningPleaseLogIn, TranslationKeys.WarningAndTryAgain);
        return of(null);
      }),
      take(1),
      untilDestroyed(this),
    );

    settingsReceived$.subscribe((settingsReceived) => {
      if (settingsReceived) {
        this.processProtocolMessage(msg);
      }
    });
  }

  public cleanup(): void {
    this.clickToCallTarget$.next(null);
  }

  private processProtocolMessage(msg: UrlProtocolMessage): void {
    let target = msg?.data?.toLowerCase()?.split(';')[0];

    this.clickToCallPrefixes.forEach((pre) => {
      if (target?.startsWith(pre)) {
        target = target.replace(`${pre}:`, '');
        this.loggingService.Info(`Received click-to-call protocol '${pre}' with target '${target}'`);
      }
    });

    const callDetails = target?.split(':')?.filter((x) => x);
    if (!callDetails?.length) {
      this.loggingService.Error(`click-to-call parsing failed: ${msg?.data}`);
      this.errorService.showError(TranslationKeys.WarningIncorrectFormat, TranslationKeys.WarningContactYourAdministrator);
      return;
    }

    let callee = decodeURIComponent(callDetails[0]);

    const type = this.getCalleeType(callee);
    if (type === undefined) {
      this.loggingService.Error(`click-to-call protocol wrong format: ${msg?.data}`);
      this.errorService.showError(TranslationKeys.WarningIncorrectFormat, TranslationKeys.WarningContactYourAdministrator);
      return;
    }

    if (type === IdentifierType.Pstn) {
      ['-', ' ', '(', ')'].forEach((x) => (callee = Utilities.replaceAll(callee, x, '')));
    }

    const teamAs = (callDetails.length > 1 ? callDetails[1] : null)?.toLowerCase();
    const team = this.services$.value?.find(
      (x) =>
        x.outboundCallState === OutboundCallState.Enabled &&
        (x.id.toLowerCase() === teamAs || x.serviceUpn.toLowerCase() === teamAs) &&
        (type !== IdentifierType.Pstn || x.pstnNumber),
    );
    if (!team) {
      if (this.services$.value?.length) {
        this.clickToCallTarget$.next(callee);
        this.router.navigate([AppRoutes.outbound]);
        return;
      }

      this.loggingService.Error(`nimbus-call no services: ${msg?.data}`);
      this.errorService.showError(TranslationKeys.WarningNoOutboundServices, TranslationKeys.WarningContactYourAdministrator);
      return;
    }

    const outboundCallAttributes = { teamId: team.id, callerIdentifier: { id: callee, type } };
    this.loggingService.Info(`nimbus-call: ${JSON.stringify(outboundCallAttributes)}`);
    this.apiService.startOutboundCall(outboundCallAttributes).pipe(untilDestroyed(this)).subscribe();
  }

  private getCalleeType(callee: string): IdentifierType {
    const guidRegexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
    if (guidRegexExp.test(callee)) {
      return IdentifierType.Guid;
    }

    const upnRegexExp = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+$/gi;
    if (upnRegexExp.test(callee)) {
      return IdentifierType.AcsUser;
    }

    const pstnRegexExp = /^[+]{0,1}[\s]*[(]{0,1}[\s]*[0-9]{1,4}[\s]*[)]{0,1}[-\s0-9]*$/gi;
    if (pstnRegexExp.test(callee)) {
      return IdentifierType.Pstn;
    }

    return undefined;
  }
}
